import { useState } from "react";
import type {
  Workflow,
  WorkflowNode,
  WorkflowNodeType,
  WorkflowTrigger,
  WorkflowTriggerType,
} from "@sales-ops-amplify/shared";
import { useUpdateWorkflow, useWorkflowStatusAction } from "@/hooks/useWorkflows";
import { cn } from "@/cn";

const TRIGGER_OPTIONS: { id: WorkflowTriggerType; label: string }[] = [
  { id: "manual", label: "Manual only" },
  { id: "schedule", label: "Schedule" },
  { id: "lead_created", label: "Lead created" },
  { id: "lead_updated", label: "Lead updated" },
  { id: "list_joined", label: "Joined a list" },
  { id: "sequence_finished", label: "Sequence finished" },
  { id: "sequence_replied", label: "Sequence replied" },
  { id: "signal_created", label: "Signal created" },
];

const ACTION_OPTIONS: { id: WorkflowNodeType; label: string }[] = [
  { id: "add_to_sequence", label: "Add to sequence" },
  { id: "remove_from_sequence", label: "Remove from sequence" },
  { id: "add_to_list", label: "Add to list" },
  { id: "remove_from_list", label: "Remove from list" },
  { id: "create_task", label: "Create task" },
  { id: "update_lead_field", label: "Update contact field" },
  { id: "assign_owner", label: "Assign owner" },
  { id: "notify_slack", label: "Notify Slack" },
  { id: "notify_email", label: "Notify email" },
  { id: "wait", label: "Wait" },
  { id: "branch", label: "Branch (filter)" },
];

function newNode(type: WorkflowNodeType): WorkflowNode {
  const id = crypto.randomUUID();
  const base: WorkflowNode = { id, type, config: {}, label: type };
  if (type === "wait") base.config = { waitDays: 1 };
  if (type === "create_task")
    base.config = { title: "Follow up", type: "action_item", priority: "medium" };
  if (type === "branch") base.config = { filters: [] };
  return base;
}

export function WorkflowBuilder({ workflow }: { workflow: Workflow }) {
  const updateMut = useUpdateWorkflow();
  const statusMut = useWorkflowStatusAction();
  const [name, setName] = useState(workflow.name);
  const [trigger, setTrigger] = useState<WorkflowTrigger>(workflow.trigger);
  const [nodes, setNodes] = useState<WorkflowNode[]>(workflow.nodes);
  const [selectedId, setSelectedId] = useState<string | null>(
    workflow.nodes[0]?.id ?? null,
  );
  const [message, setMessage] = useState<string | null>(null);

  const selected = nodes.find((n) => n.id === selectedId) ?? null;

  function rewire(list: WorkflowNode[]): WorkflowNode[] {
    return list.map((n, i) => ({
      ...n,
      next: n.type === "branch" ? n.next : (list[i + 1]?.id ?? null),
    }));
  }

  function addAction(type: WorkflowNodeType) {
    const node = newNode(type);
    setNodes((prev) => rewire([...prev, node]));
    setSelectedId(node.id);
  }

  function updateSelected(patch: Partial<WorkflowNode>) {
    if (!selected) return;
    setNodes((prev) =>
      prev.map((n) => (n.id === selected.id ? { ...n, ...patch } : n)),
    );
  }

  function removeSelected() {
    if (!selected) return;
    setNodes((prev) => rewire(prev.filter((n) => n.id !== selected.id)));
    setSelectedId(null);
  }

  async function save() {
    setMessage(null);
    try {
      const wired = rewire(nodes);
      await updateMut.mutateAsync({
        workflowId: workflow.workflowId,
        patch: {
          name,
          trigger,
          nodes: wired,
          entryNodeId: wired[0]?.id ?? null,
        },
      });
      setNodes(wired);
      setMessage("Saved");
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Save failed");
    }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
      <div className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <label className="block min-w-[200px] flex-1 text-sm">
            <span className="text-muted-foreground">Workflow name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={updateMut.isPending}
              onClick={() => void save()}
              className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted"
            >
              Save
            </button>
            {workflow.status !== "active" ? (
              <button
                type="button"
                disabled={statusMut.isPending}
                onClick={async () => {
                  await save();
                  await statusMut.mutateAsync({
                    workflowId: workflow.workflowId,
                    action: "activate",
                  });
                }}
                className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground"
              >
                Activate
              </button>
            ) : (
              <button
                type="button"
                disabled={statusMut.isPending}
                onClick={() =>
                  statusMut.mutate({
                    workflowId: workflow.workflowId,
                    action: "deactivate",
                  })
                }
                className="rounded-md border border-border px-3 py-1.5 text-sm"
              >
                Deactivate
              </button>
            )}
          </div>
        </div>

        <section className="rounded-lg border border-border p-4">
          <h3 className="text-sm font-semibold">Trigger</h3>
          <select
            value={trigger.type}
            onChange={(e) => {
              const type = e.target.value as WorkflowTriggerType;
              setTrigger((t) => ({
                ...t,
                type,
                scheduleInterval:
                  type === "schedule" ? t.scheduleInterval ?? "weekly" : undefined,
                scheduleDayOfWeek:
                  type === "schedule" ? t.scheduleDayOfWeek ?? 1 : undefined,
                scheduleTime: type === "schedule" ? t.scheduleTime ?? "09:00" : undefined,
              }));
            }}
            className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            {TRIGGER_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
          {trigger.type === "schedule" && (
            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              <select
                value={trigger.scheduleInterval ?? "weekly"}
                onChange={(e) =>
                  setTrigger((t) => ({
                    ...t,
                    scheduleInterval: e.target.value as "daily" | "weekly",
                  }))
                }
                className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
              <input
                type="number"
                min={0}
                max={6}
                value={trigger.scheduleDayOfWeek ?? 1}
                onChange={(e) =>
                  setTrigger((t) => ({
                    ...t,
                    scheduleDayOfWeek: Number(e.target.value),
                  }))
                }
                className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                title="Day of week (0=Sun)"
              />
              <input
                value={trigger.scheduleTime ?? "09:00"}
                onChange={(e) =>
                  setTrigger((t) => ({ ...t, scheduleTime: e.target.value }))
                }
                className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              />
            </div>
          )}
          {(trigger.type === "list_joined" ||
            trigger.type === "sequence_finished" ||
            trigger.type === "sequence_replied" ||
            trigger.type === "lead_updated") && (
            <input
              placeholder={
                trigger.type === "list_joined"
                  ? "List ID"
                  : trigger.type.startsWith("sequence")
                    ? "Sequence ID (optional)"
                    : "Field key (e.g. renewalDate)"
              }
              value={
                trigger.listId ||
                trigger.sequenceId ||
                trigger.fieldKey ||
                ""
              }
              onChange={(e) => {
                const v = e.target.value;
                setTrigger((t) => ({
                  ...t,
                  listId: t.type === "list_joined" ? v : t.listId,
                  sequenceId: t.type.startsWith("sequence") ? v : t.sequenceId,
                  fieldKey: t.type === "lead_updated" ? v : t.fieldKey,
                }));
              }}
              className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          )}
        </section>

        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Actions</h3>
            <select
              defaultValue=""
              onChange={(e) => {
                const v = e.target.value as WorkflowNodeType;
                if (v) addAction(v);
                e.target.value = "";
              }}
              className="rounded-md border border-border bg-background px-2 py-1 text-xs"
            >
              <option value="">Add block…</option>
              {ACTION_OPTIONS.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          {nodes.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              Add action blocks to define what happens after the trigger.
            </div>
          ) : (
            <ol className="space-y-2">
              {nodes.map((n, i) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(n.id)}
                    className={cn(
                      "w-full rounded-lg border px-3 py-2.5 text-left text-sm",
                      selectedId === n.id
                        ? "border-brand bg-brand/5"
                        : "border-border hover:bg-muted/40",
                    )}
                  >
                    <span className="text-xs text-muted-foreground">
                      Step {i + 1}
                    </span>
                    <p className="font-medium">
                      {n.label || n.type.replace(/_/g, " ")}
                    </p>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </section>
        {message && (
          <p className="text-sm text-muted-foreground">{message}</p>
        )}
      </div>

      <aside className="rounded-lg border border-border p-4">
        <h3 className="text-sm font-semibold">Block config</h3>
        {!selected ? (
          <p className="mt-2 text-sm text-muted-foreground">
            Select a block to configure.
          </p>
        ) : (
          <div className="mt-3 space-y-3 text-sm">
            <label className="block">
              <span className="text-muted-foreground">Label</span>
              <input
                value={selected.label ?? ""}
                onChange={(e) => updateSelected({ label: e.target.value })}
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
              />
            </label>
            {(selected.type === "add_to_sequence" ||
              selected.type === "remove_from_sequence") && (
              <label className="block">
                <span className="text-muted-foreground">Sequence ID</span>
                <input
                  value={String(selected.config.sequenceId ?? "")}
                  onChange={(e) =>
                    updateSelected({
                      config: { ...selected.config, sequenceId: e.target.value },
                    })
                  }
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                />
              </label>
            )}
            {(selected.type === "add_to_list" ||
              selected.type === "remove_from_list") && (
              <label className="block">
                <span className="text-muted-foreground">List ID</span>
                <input
                  value={String(selected.config.listId ?? "")}
                  onChange={(e) =>
                    updateSelected({
                      config: { ...selected.config, listId: e.target.value },
                    })
                  }
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                />
              </label>
            )}
            {selected.type === "create_task" && (
              <label className="block">
                <span className="text-muted-foreground">Task title</span>
                <input
                  value={String(selected.config.title ?? "")}
                  onChange={(e) =>
                    updateSelected({
                      config: { ...selected.config, title: e.target.value },
                    })
                  }
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                />
              </label>
            )}
            {selected.type === "wait" && (
              <label className="block">
                <span className="text-muted-foreground">Wait days</span>
                <input
                  type="number"
                  min={0}
                  value={Number(selected.config.waitDays ?? 1)}
                  onChange={(e) =>
                    updateSelected({
                      config: {
                        ...selected.config,
                        waitDays: Number(e.target.value),
                      },
                    })
                  }
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                />
              </label>
            )}
            {selected.type === "notify_slack" && (
              <label className="block">
                <span className="text-muted-foreground">Message</span>
                <textarea
                  value={String(selected.config.message ?? "")}
                  onChange={(e) =>
                    updateSelected({
                      config: { ...selected.config, message: e.target.value },
                    })
                  }
                  rows={3}
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                />
              </label>
            )}
            <button
              type="button"
              onClick={removeSelected}
              className="text-xs text-destructive hover:underline"
            >
              Remove block
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}
